import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Button } from '@design-system/Button';
import { DataForm } from '@design-system/DataForm';
import { TextField } from '@design-system/DataForm/TextField';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSendReportMutation } from '@viome/viome-base/src/usecase/Misc/SendReportMutation';
import { cn } from '@/utils/styles';
import { useState, useEffect } from 'react';
import { useUserStore } from '@/hooks/useUserStore';
import { LocalStorage, KEYS } from '@/utils/LocalStorage';
import { KitType } from '@viome/viome-base/src/usecase/Bootup/BootupModel';

const schema = z.object({
  email: z.string().email(),
});

type FormData = z.infer<typeof schema>;

export const SendMyReportsPageReportFormSection = () => {
  const { mutate: sendReport, isPending } = useSendReportMutation();
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = useUserStore((state) => state.user);
  
  // Get kit type from local storage
  const localStorageUser = LocalStorage.getItem(KEYS.BOOTUP);
  const bootupState = localStorageUser ? JSON.parse(localStorageUser) : null;
  const kitType = bootupState?.kitType;

  const form = useForm<FormData>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      email: user?.email || '',
    },
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isSuccess) {
      timeoutId = setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isSuccess]);

  const onSubmit = (data: FormData) => {
    setError(null);

    // Determine report type based on kit type
    const reportType = kitType === KitType.PHPRO ? 'PHPRO_REPORT' : 'R';

    const reportData = {
      ...data,
      report_type: reportType,
    };

    sendReport(reportData, {
      onSuccess: () => {
        setIsSuccess(true);
        form.reset({ email: '' });
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to send report. Please try again.';
        setError(errorMessage);
        form.reset({ email: '' });
      },
    });
  };

  const isPhpro = kitType === KitType.PHPRO;

  const buttonText = isPending
    ? 'Sending...'
    : isSuccess
      ? 'Sent!'
      : 'Send most recent report';

  return (
    <div data-testid="sendReportsForm-container">
      <DataForm form={form} onValid={onSubmit} data-testid="sendReportsForm-form">
        <TextField
          name="email"
          placeholder="Email"
          variant="secondary"
          onChange={() => {
            if (error) setError(null);
          }}
          data-testid="sendReportsForm-emailField"
        />

        {error && <div className="text-red-500 text-sm mt-1 mb-2" data-testid="sendReportsForm-error">{error}</div>}

        <Button
          type="submit"
          disabled={isPending || isPhpro}
          className={cn(
            'w-full',
            isSuccess && '!bg-background-success',
            (isPending || isPhpro) && 'opacity-50',
          )}
          data-testid="sendReportsForm-submitButton"
        >
          {buttonText}
        </Button>

        {isPhpro && (
          <p className="typography-caption text-secondary mt-3" data-testid="sendReportsForm-phpro-note">
            To access your Precision Health PRO™ report in PDF, please contact your healthcare provider.
          </p>
        )}
      </DataForm>
    </div>
  );
};
